import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';

export default function AnalyticsPage() {
  const features = [
    'Google Analytics 4 Setup',
    'Conversion Tracking',
    'Call Tracking',
    'Form Submission Tracking',
    'Patient Journey Mapping',
    'ROI Attribution',
    'Custom Dashboards',
    'Automated Reporting',
    'Competitor Benchmarking',
    'A/B Testing',
    'Heatmaps & Session Recording',
    'Monthly Strategy Calls'
  ];

  const metrics = [
    {
      icon: 'ri-user-add-line',
      title: 'Patient Acquisition Cost',
      description: 'Track exactly how much you spend to acquire each new patient across all marketing channels.'
    },
    {
      icon: 'ri-funds-line',
      title: 'Marketing ROI',
      description: 'Measure the return on investment for every marketing dollar spent with clear attribution.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Conversion Rates',
      description: 'Monitor how many website visitors convert into leads, appointments, and patients.'
    },
    {
      icon: 'ri-phone-line',
      title: 'Call & Form Conversions',
      description: 'Track phone calls and form submissions generated by each marketing campaign.'
    },
    {
      icon: 'ri-map-pin-user-line',
      title: 'Patient Journey',
      description: 'Understand the complete path patients take from first touch to booking an appointment.'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Campaign Performance',
      description: 'Compare performance across SEO, PPC, social media, and other channels to optimize budget.'
    }
  ];

  const benefits = [
    {
      icon: 'ri-eye-line',
      title: 'Complete Visibility',
      description: 'See exactly where your patients come from and which marketing efforts drive the best results.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Maximize ROI',
      description: 'Invest more in what works and eliminate what doesn\'t based on real data, not guesswork.'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Data-Driven Decisions',
      description: 'Make strategic marketing decisions based on actual performance data and patient behavior.'
    },
    {
      icon: 'ri-focus-3-line',
      title: 'Identify Opportunities',
      description: 'Discover untapped opportunities and areas for improvement in your marketing funnel.'
    },
    {
      icon: 'ri-speed-up-line',
      title: 'Faster Optimization',
      description: 'Quickly identify and fix issues, test improvements, and accelerate growth with real-time data.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'HIPAA-Compliant',
      description: 'All tracking and analytics implementation follows healthcare privacy guidelines and regulations.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Audit & Setup',
      description: 'Review current tracking, identify gaps, and implement comprehensive analytics across all platforms.'
    },
    {
      step: '02',
      title: 'Goal Configuration',
      description: 'Define and configure tracking for key conversions: calls, forms, appointments, and patient actions.'
    },
    {
      step: '03',
      title: 'Dashboard Creation',
      description: 'Build custom dashboards that display the metrics that matter most to your practice growth.'
    },
    {
      step: '04',
      title: 'Attribution Setup',
      description: 'Implement multi-touch attribution to understand the complete patient journey and ROI.'
    },
    {
      step: '05',
      title: 'Reporting Automation',
      description: 'Set up automated monthly reports delivered to your inbox with key insights and recommendations.'
    },
    {
      step: '06',
      title: 'Ongoing Analysis',
      description: 'Continuous monitoring, analysis, and strategic recommendations to improve performance.'
    }
  ];

  const reports = [
    'Website Traffic & Sources',
    'Lead Generation Performance',
    'Conversion Rate Analysis',
    'Campaign ROI Breakdown',
    'Patient Acquisition Cost',
    'Channel Performance Comparison',
    'Landing Page Effectiveness',
    'Call & Form Tracking',
    'SEO Rankings & Traffic',
    'PPC Campaign Performance',
    'Social Media Engagement',
    'Competitor Benchmarking'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                ANALYTICS &amp; ROI REPORTING
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Know Exactly What's Working &amp; What's Not
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                HIPAA-compliant analytics and reporting that tracks every patient touchpoint, measures true ROI, and provides actionable insights to grow your practice.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap font-bold"
                >
                  <span>Get Your Analytics Audit</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="#process"
                  className="inline-flex items-center gap-2 bg-white text-[#7C3AED] border-2 border-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED]/5 transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  <span>See Our Process</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/cefef6ea024f8efcd56f833c43ad8778.jpg"
                  alt="Analytics dashboard"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              KEY METRICS WE TRACK
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              The Numbers That Actually Matter
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              We focus on the metrics that directly impact your practice growth and profitability, not vanity metrics.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {metrics.map((metric, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${metric.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {metric.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {metric.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              WHY ANALYTICS MATTER
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Stop Guessing, Start Growing
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Proper analytics transform your marketing from guesswork into a predictable, scalable patient acquisition system.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reports Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                COMPREHENSIVE REPORTING
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Clear, Actionable Reports Delivered Monthly
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                Get detailed monthly reports that show exactly what's working, what's not, and what to do next to grow your practice.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {reports.map((report, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#7C3AED] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{report}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Marketing%20analytics%20report%20showing%20detailed%20performance%20metrics%20ROI%20tracking%20patient%20acquisition%20data%20and%20campaign%20results%20for%20healthcare%20practice%20professional%20reporting%20dashboard%20purple%20accents%20clean%20modern%20design&width=600&height=700&seq=analyticsreport1&orientation=portrait"
                  alt="Analytics reports"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Analytics%20tracking%20setup%20showing%20Google%20Analytics%20conversion%20tracking%20call%20tracking%20and%20patient%20journey%20mapping%20for%20healthcare%20marketing%20professional%20analytics%20implementation%20interface%20purple%20accents%20clean%20modern%20design&width=600&height=700&seq=analyticsfeatures1&orientation=portrait"
                  alt="Analytics features"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                COMPLETE ANALYTICS SOLUTION
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Everything You Need to Track Performance
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                From initial setup to ongoing reporting, we provide comprehensive analytics that give you complete visibility into your marketing performance.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#7C3AED] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              OUR PROCESS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              From Setup to Ongoing Insights
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              We handle everything from analytics implementation to monthly reporting and strategic recommendations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-xl flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Ready to Get Clear on Your Marketing ROI?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            Get a free analytics audit showing what's currently tracked, what's missing, and how to measure true marketing ROI.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="/book-strategy-call"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
            >
              <span>Get Your Analytics Audit</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <span>See Sample Reports</span>
            </a>
          </div>
        </div>
      </section>

      <RelatedServices currentSlug="analytics-reporting" />
      <RelatedIndustries />

      <Footer />
    </div>
  );
}
